import { Component, ErrorHandler, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Router } from '@angular/router'

import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'

import { MoniyritystukiComponentDataResolve } from 'app/_angular/_resolvers/moniyritystuki.resolve'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { HaeMoniyritystuetVastaus, MoniyritystuenDuplikaatti, Moniyritystuki, MuodostaKaikkiMoniyritystuetVastaus } from 'app/_jaettu-lemonator/model/moniyritys'

@Component({
  templateUrl: './moniyritystuet.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MoniyritystuetComponent implements OnInit {
  @ViewChild(MatSort, { static: true }) _sort: MatSort

  dataSource = new MatTableDataSource<Moniyritystuki>([])
  readonly loading = signal(true)
  readonly commonError = signal<string | null>(null)
  readonly muodostusKaynnissa = signal(false)
  readonly duplikaatit = signal<MoniyritystuenDuplikaatti[]>([])

  displayedColumns: string[] = ['nimi', 'hetu', 'yritykset']

  constructor(
    private router: Router,
    private errorHandler: ErrorHandler,
    private _firebase: FirebaseLemonator,
    private _ladataanService: LadataanService,
    private _snackbar: MatSnackBar,
    private _moniyritystukiComponentDataResolve: MoniyritystukiComponentDataResolve,
  ) {
    this.dataSource.sortData = (data: Moniyritystuki[], sort: MatSort): Moniyritystuki[] => {
      const active = sort.active
      const direction = sort.direction

      if (!active || direction === '') {
        return data
      }

      const directionMultiplier = direction === 'asc' ? 1 : -1
      const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

      if (active === 'nimi') {
        return data.sort((a, b) => collator.compare(a.nimi, b.nimi) * directionMultiplier)
      }
      if (active === 'yritykset') {
        return data.sort((a, b) => collator.compare(this.annaYritykset(a), this.annaYritykset(b)) * directionMultiplier)
      }

      return data
    }
  }

  async ngOnInit() {
    this._sort.sort({ id: 'nimi', start: 'asc', disableClear: false })
    this.dataSource.sort = this._sort
    await this._lataa()
  }

  private async _lataa() {
    this.loading.set(true)
    try {
      const vastaus = await this._firebase.functionsCall<void, HaeMoniyritystuetVastaus>('moniyritystuki-haeKaikki', undefined)
      if (vastaus?.e) {
        this.commonError.set('Moniyritystukien lataaminen epäonnistui. Yritä uudelleen.')
        return
      }
      this.dataSource.data = vastaus?.tuet ?? []
    } catch (err) {
      this.commonError.set('Moniyritystukien lataaminen epäonnistui. Yritä uudelleen.')
      this.errorHandler.handleError(err)
    } finally {
      this.loading.set(false)
    }
  }

  annaYritykset(tuki: Moniyritystuki): string {
    return (tuki.osalliset || []).map(osallinen => osallinen.asiakasNimi).join(', ')
  }

  annaMaskattuHetu(tuki: Moniyritystuki): string {
    return tuki.hetu ? `${tuki.hetu.substring(0, 6)}XXXXX` : ''
  }

  async muodostaKaikki() {
    if (this.muodostusKaynnissa()) {
      return
    }
    this.muodostusKaynnissa.set(true)
    this.commonError.set(null)
    this.duplikaatit.set([])
    this._ladataanService.aloitaLataaminen()
    try {
      const vastaus = await this._firebase.functionsCall<void, MuodostaKaikkiMoniyritystuetVastaus>('moniyritystuki-muodostaKaikki', undefined, { timeout: 540 * 1000 })
      if (vastaus?.e) {
        this.commonError.set(vastaus.e === 'unauthorized' ? 'Ei oikeuksia muodostaa moniyritystukia.' : 'Muodostus epäonnistui. Yritä uudelleen.')
        return
      }
      this.duplikaatit.set(vastaus?.duplikaatit ?? [])
      this._snackbar.open(
        `Käsitelty ${vastaus?.kasiteltyjaHetuja ?? 0} henkilöä: luotu ${vastaus?.luotu ?? 0}, päivitetty ${vastaus?.paivitetty ?? 0}, purettu ${vastaus?.purettu ?? 0}, ennallaan ${vastaus?.ennallaan ?? 0}.`,
        'OK',
        { duration: 10000 },
      )
      await this._lataa()
    } catch (err) {
      this.commonError.set('Muodostus epäonnistui. Yritä uudelleen.')
      this.errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
      this.muodostusKaynnissa.set(false)
    }
  }

  muokkaa(tuki: Moniyritystuki) {
    this._moniyritystukiComponentDataResolve.asetaOlemassaolevaData({ tuki: tuki })
    this.router.navigate(['yllapito', 'moniyritystuet', tuki.avain])
  }
}
